import {
    isCodeListValid,
    multipleContainersListDefinition,
    multipleLocationsListDefinition,
    ScanCodeListDefinition,
    ScannedCode,
    ScannedCodeType,
    scannedCodeTypes,
    ScannedCodeWithEntity,
    singleActivityListDefinition,
    singleContainerListDefinition,
    singleLocationsListDefinition,
} from 'app/features/scan-flow/models';
import { singleBatchListDefinition } from 'app/features/scan-flow/models/scanned-code-list-definition';
import {
    isBatchRequired,
    isLocationTypeNullOrSite,
    isOneOrManyContainersRequired,
    isOneOrManyLocationRequired,
    isOneOrManyProductBoxRegistrationsRequired,
    isOneOrManyProductBoxRequired,
    isSingleAmountResultRequired,
    isSingleContainerRequired,
    isSingleLocationRequired,
} from 'app/features/scan-flow/utils';
import {
    isContainerMeasurementRegistrationDataComplete,
    isResultRegistrationActivity,
} from 'app/features/scan-flow/utils/result-registration/container-measurement/scan-container-measurement.utils';
import { isPackingActivity } from 'app/features/scan-flow/utils/scan-activity.utils';
import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import { MeasurementType } from 'app/shared/models/measurement-type';
import {
    areProductBoxRegistrationsAmountValid,
    getProductBoxRegistrationByBoxNumber,
    isAmountResultValidOrEmpty,
    isProductBoxRegistrationValid,
    resetProductBoxRegistrationAmounts,
} from 'app/shared/utils';
import { ScanFlowRegistrationData } from 'app/state/scan-flow/scan-flow.reducer';

interface IHasCodeType {
    codeType: ScannedCodeType;
}

export function filterByCodeType<T extends IHasCodeType>(list: T[], codeType: ScannedCodeType) {
    return list
        .filter((scannedCode) => scannedCode !== null)
        .filter((scannedCode) => scannedCode.codeType === codeType);
}

export function getFirstByCodeType<T extends IHasCodeType>(list: T[], codeType: ScannedCodeType) {
    return filterByCodeType(list, codeType).find((i) => !!i);
}

export function isScanFlowRegistrationDataComplete({
    scanCodeListDefinitions,
    registrationData: { scannedCodes, employeeNumber, amountResult, productBoxRegistrations },
}: {
    scanCodeListDefinitions: ScanCodeListDefinition[];
    registrationData: ScanFlowRegistrationData;
}): boolean {
    const activity = getActivityCode(scannedCodes)?.entity;
    if (isResultRegistrationActivity(activity)) {
        return isContainerMeasurementRegistrationDataComplete(
            employeeNumber,
            scanCodeListDefinitions,
            scannedCodes,
            activity,
            amountResult,
            productBoxRegistrations,
        );
    }
    return (
        !!employeeNumber &&
        !!scanCodeListDefinitions.length &&
        scannedCodeTypes.every((codeType: ScannedCodeType) => {
            const listDefinition = getFirstByCodeType(scanCodeListDefinitions, codeType);
            const scannedCodesByCodeType = filterByCodeType(scannedCodes, codeType);
            return (
                (!listDefinition && !scannedCodesByCodeType.length) ||
                (!!listDefinition && isCodeListValid(listDefinition, scannedCodesByCodeType))
            );
        }) &&
        isSingleAmountResultRequired(activity) === isAmountResultProvided(amountResult) &&
        isAmountResultValidOrEmpty({ amountResult, activity }) &&
        (isOneOrManyProductBoxRegistrationsRequired(activity) || isOneOrManyProductBoxRequired(activity)) ===
            !!productBoxRegistrations.length &&
        areProductBoxRegistrationsValidOrEmpty(productBoxRegistrations, activity)
    );
}

function isAmountResultProvided(amountResult: number | undefined | null) {
    return amountResult !== undefined || amountResult === 0 || amountResult === null;
}

export function isScanFlowRegistrationDataInProgress({
    scanCodeListDefinitions,
    registrationData,
}: {
    scanCodeListDefinitions: ScanCodeListDefinition[];
    registrationData: ScanFlowRegistrationData;
}) {
    const activity = getActivityCode(registrationData.scannedCodes)?.entity;
    const hasScannedCodes = scannedCodeTypes.some(
        (codeType) =>
            codeType !== 'activity' &&
            codeType !== 'batch' &&
            !!filterByCodeType(registrationData.scannedCodes, codeType).length,
    );
    const hasProductBoxRegistrations = !!registrationData.productBoxRegistrations.length;

    // Only for activity with resultDeterminationType byContainerAndProductBox, it requires at least location/containers to be filled
    if (isOneOrManyProductBoxRequired(activity)) {
        return (
            !isScanFlowRegistrationDataComplete({ registrationData, scanCodeListDefinitions }) &&
            hasScannedCodes &&
            hasProductBoxRegistrations
        );
    }

    if (isResultRegistrationActivity(activity)) {
        const hasScannedContainerCode = !!filterByCodeType(registrationData.scannedCodes, 'container').length;
        // For container measurement activity, it requires at least container code to be scanned
        // Once container is filled, it requires at least amountResult or productBoxRegistrations to be filled to be considered in progress
        return (
            !isScanFlowRegistrationDataComplete({ registrationData, scanCodeListDefinitions }) &&
            hasScannedContainerCode &&
            (isAmountResultProvided(registrationData.amountResult) ||
                registrationData.productBoxRegistrations.some(({ amount }) => isAmountResultProvided(amount)))
        );
    }
    // By default, as long as any code is scanned or amount filled, it's considered as in progress
    return (
        !isScanFlowRegistrationDataComplete({ registrationData, scanCodeListDefinitions }) &&
        (hasScannedCodes ||
            isAmountResultProvided(registrationData.amountResult) ||
            registrationData.productBoxRegistrations.some(({ amount }) => isAmountResultProvided(amount)))
    );
}

export function getActivityCode(scannedCodes: ScannedCode[]): ScannedCodeWithEntity<Activity> | undefined {
    const scannedCode = getFirstByCodeType(scannedCodes, 'activity');

    return scannedCode ? (scannedCode as ScannedCodeWithEntity<Activity>) : undefined;
}

export function getBatchCode(scannedCodes: ScannedCode[]): ScannedCode | undefined {
    const scannedCode = getFirstByCodeType(scannedCodes, 'batch');

    return scannedCode ? (scannedCode as ScannedCode) : undefined;
}

export function sortScannedCodes(codes: ScannedCode[]) {
    return [...codes].sort((a, b) => a.code.localeCompare(b.code));
}

export function takeValidScannedCodesForNewActivity(
    oldScannedCodes: ScannedCode[],
    newScannedActivityCode: ScannedCodeWithEntity<Activity> | undefined,
    isLocationTypeSimilar?: boolean,
) {
    let newCodes: ScannedCode[] = [];
    const newFlowCodeDefinitions = getScanFlowCodeDefinitionsForActivity(newScannedActivityCode?.entity);
    for (const { codeType, maxLength } of newFlowCodeDefinitions) {
        if (codeType === 'activity') {
            if ((!maxLength || maxLength > 0) && newScannedActivityCode) {
                newCodes = [...newCodes, newScannedActivityCode];
            }
        } else if (codeType === 'batch') {
            if (!maxLength || maxLength > 0) {
                const oldCodesOfBatchType = filterByCodeType(oldScannedCodes, 'batch');
                newCodes = [...newCodes, ...oldCodesOfBatchType];
            }
        } else if (codeType === 'location') {
            // Check if new Activity LocationType is the same as current Activity
            // If it's the same, then retain location information
            // Otherwise, ignore the locations as different location types have different set of location ID's
            if (isLocationTypeSimilar) {
                const oldCodesOfLocationType = filterByCodeType(oldScannedCodes, 'location');
                const locationCodes = oldCodesOfLocationType.slice(0, maxLength);
                newCodes = [...newCodes, ...locationCodes];
            }
        } else {
            const oldCodesOfCurrentType = filterByCodeType(oldScannedCodes, codeType);
            const remainingCodes = oldCodesOfCurrentType.slice(0, maxLength);
            newCodes = [...newCodes, ...remainingCodes];
        }
    }
    return sortScannedCodes(newCodes);
}

export function isMultiStepScanFlow(activity: Activity) {
    return (
        isSingleLocationRequired(activity) ||
        isOneOrManyLocationRequired(activity) ||
        isOneOrManyContainersRequired(activity) ||
        isSingleContainerRequired(activity) ||
        isSingleAmountResultRequired(activity) ||
        isOneOrManyProductBoxRegistrationsRequired(activity) ||
        isResultRegistrationActivity(activity) ||
        isBatchRequired(activity)
    );
}

export function getNextActivityAfterRegistration({
    currentActivity,
}: {
    currentActivity: Activity | undefined;
}) {
    if (!currentActivity) return undefined;

    if (isMultiStepScanFlow(currentActivity)) {
        return currentActivity;
    }

    return undefined;
}

export function getScanFlowCodeDefinitionsForActivity(
    activity: Activity | undefined,
): ScanCodeListDefinition[] {
    let codeDefinitions = [singleActivityListDefinition];

    if (!activity) return codeDefinitions;

    if (isBatchRequired(activity)) {
        codeDefinitions = [...codeDefinitions, singleBatchListDefinition];
    }

    if (!isLocationTypeNullOrSite(activity)) {
        if (isSingleLocationRequired(activity)) {
            codeDefinitions = [...codeDefinitions, singleLocationsListDefinition];
        } else if (isOneOrManyLocationRequired(activity)) {
            codeDefinitions = [...codeDefinitions, multipleLocationsListDefinition];
        }
    }

    if (
        isSingleContainerRequired(activity) ||
        (isPackingActivity(activity) && activity.measurementType !== MeasurementType.noMeasurementType)
    ) {
        codeDefinitions = [...codeDefinitions, singleContainerListDefinition];
    } else if (isOneOrManyContainersRequired(activity)) {
        codeDefinitions = [...codeDefinitions, multipleContainersListDefinition];
    }

    return codeDefinitions;
}

export function getAllowedCodeTypes({
    scanFlowCodeListDefinitions,
    scannedCodes,
}: {
    scanFlowCodeListDefinitions: ScanCodeListDefinition[];
    scannedCodes: ScannedCode[];
}) {
    return scannedCodeTypes.filter((codeType) => {
        if (codeType === 'activity') return true;

        const listDefinition = getFirstByCodeType(scanFlowCodeListDefinitions, codeType);

        if (!listDefinition) return false;

        if (!listDefinition.maxLength) return true;

        // For batch, we allow multiple batches to be scanned
        if (codeType === 'batch') return true;

        const currentCodes = filterByCodeType(scannedCodes, codeType);

        return currentCodes.length < listDefinition.maxLength;
    });
}

export function areProductBoxRegistrationsValidOrEmpty(
    productBoxRegistrations: ProductBoxRegistration[],
    activity: Activity | undefined,
) {
    return (
        !productBoxRegistrations.length ||
        productBoxRegistrations.every((productBoxRegistration) =>
            isProductBoxRegistrationValid({ productBoxRegistration, activity }),
        ) ||
        areProductBoxRegistrationsAmountValid(productBoxRegistrations, activity?.resultDeterminationType)
    );
}

export function getProductBoxRegistrationsForNewActivity({
    newActivity,
    keepRegistrationData,
    currentProductBoxRegistrations,
}: {
    newActivity: Activity;
    keepRegistrationData: boolean;
    currentProductBoxRegistrations: ProductBoxRegistration[];
}): ProductBoxRegistration[] {
    if (!isOneOrManyProductBoxRegistrationsRequired(newActivity)) {
        return [];
    }

    let newProductBoxRegistrations = [...currentProductBoxRegistrations];

    if (!keepRegistrationData) {
        newProductBoxRegistrations = resetProductBoxRegistrationAmounts(newProductBoxRegistrations);
    }

    if (!newActivity.defaultProductBoxNumber) return newProductBoxRegistrations;

    const amountDefaultProductBox = getProductBoxRegistrationByBoxNumber(
        newProductBoxRegistrations,
        newActivity.defaultProductBoxNumber,
    )?.amount;

    return [{ productBoxNumber: newActivity.defaultProductBoxNumber, amount: amountDefaultProductBox }];
}
